import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    GripVertical,
    CheckCircle2,
    CheckCircle,
    MapPin,
    Navigation,
    Eye,
    AlertCircle,
    AlertTriangle,
    MessageCircle,
    RotateCcw
} from 'lucide-react';
import { computeCollectionAmounts, isCreditOrder } from '../utils/payments';

/**
 * Componente separado para cada orden sortable
 * Rediseñado para layout vertical tipo tarjeta (Mobile First)
 */
const SortableOrderCard = ({
    order,
    route,
    isDelivered,
    isFailed, // 🆕 Estado de entrega fallida
    isReturned, // 🆕 Retorno validado por cajera
    setSelectedOrderDetails,
    setSelectedOrderForFailed,
    setSelectedOrderTimeline,
    onEditAddress, // 🆕
    onValidateReturn // 🆕 Validar retorno físico
}) => {
    // Hook de sortable
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: order.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.8 : 1,
        zIndex: isDragging ? 50 : 'auto',
    };

    const canDrag = !isDelivered && !isFailed && !isReturned && (route.status === 'open' || route.status === 'in_progress');

    // Cálculos de saldo
    const { totalDue } = computeCollectionAmounts(order);
    const hasBalance = totalDue > 0;
    const isCredit = isCreditOrder(order);

    // Determinación de colores de borde y estado
    let borderClass = 'border-l-4 border-l-emerald-500'; // Por defecto: Verde (Listo/Pagado)
    if (hasBalance) borderClass = 'border-l-4 border-l-red-500'; // Rojo: Saldo pendiente
    if (isFailed) borderClass = 'border-l-4 border-l-amber-500'; // Naranja: Entrega fallida
    if (isReturned) borderClass = 'border-l-4 border-l-purple-500'; // Morado: Retornado
    if (isDelivered) borderClass = 'border-l-4 border-l-green-600'; // Verde fuerte: Entregado

    const totalAmount = (Number(order.totalAmount) || 0) + (Number(order.internal_orders_total) || 0);
    const paidAmount = Number(order.paid_amount ?? order.paidAmount ?? order.amount_paid ?? 0);

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            className={`
                relative rounded-lg shadow-sm border 
                mb-2 text-left overflow-hidden touch-manipulation
                ${borderClass}
                ${isDragging ? 'shadow-2xl scale-[1.02] ring-2 ring-blue-400 rotate-1' : ''}
                ${isDelivered ? 'bg-emerald-50 border-emerald-200' : isReturned ? 'bg-purple-50 border-purple-200' : isFailed ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-100'}
            `}
        >
            {/* Botón de Navegación Flotante (Esquina Superior Derecha) */}
            {!isDelivered && !isFailed && !isReturned && (
                <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(order.deliveryAddress)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="absolute top-2 right-2 p-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors z-10 shadow-sm border border-blue-100"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Navigation className="w-4 h-4" />
                </a>
            )}

            {/* Contenido Principal */}
            <div className="p-3 pb-2 pr-10"> {/* Padding derecho extra para el botón de mapa */}

                {/* Header: #Pedido Grande + Nombre */}
                <div className="flex items-start gap-3 mb-1">
                    {/* Badge de Secuencia */}
                    <div className={`
                        w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black flex-shrink-0 shadow-sm mt-0.5
                        ${isDelivered ? 'bg-emerald-600 text-white' : isReturned ? 'bg-purple-500 text-white' : isFailed ? 'bg-amber-500 text-white' : 'bg-blue-600 text-white'}
                    `}>
                        {isDelivered ? <CheckCircle className="w-3.5 h-3.5" /> : isReturned ? <RotateCcw className="w-3.5 h-3.5" /> : isFailed ? <AlertTriangle className="w-3.5 h-3.5" /> : order.sequenceNumber}
                    </div>

                    <div className="flex flex-col min-w-0">
                        {/* Pedido Grande y Elegante */}
                        <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="text-xl font-black text-gray-800 tracking-tight leading-none">
                                #{order.orderNumber}
                            </span>
                            {Number(order.internal_orders_total) > 0 && order.internal_order_numbers && (
                                <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-1.5 rounded border border-amber-200 -translate-y-0.5 shadow-sm">
                                    +{String(order.internal_order_numbers).split(',').join(', ')}
                                </span>
                            )}
                        </div>
                        {/* Nombre del Cliente */}
                        <h3 className="font-bold text-xs text-gray-500 truncate uppercase mt-0.5">
                            {order.customerName || 'Cliente Sin Nombre'}
                        </h3>
                    </div>
                </div>

                {/* Dirección Compacta + Botón Editar */}
                <div className="flex items-start gap-1.5 ml-9 mb-2 text-gray-400">
                    <span className="text-[10px] font-medium leading-tight line-clamp-1 flex-1">
                        {order.deliveryAddress || 'Sin dirección registrada'}
                    </span>
                    {onEditAddress && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onEditAddress(order);
                            }}
                            className="p-1 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="Editar Dirección"
                        >
                            {/* Pencil Icon inline */}
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></svg>
                        </button>
                    )}
                </div>

                {/* Detalle de Pedidos (Padre + Hijos) */}
                <div className="flex flex-col ml-9 border-t border-gray-50 pt-2 mt-1">

                    {/* Lista de Pedidos (Padre e Hijos) */}
                    <div className="space-y-1 mb-2">
                        {/* Pedido Padre */}
                        <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-600 font-medium">Principal ({order.orderNumber})</span>
                            <span className="text-gray-900 font-bold">${Number(order.totalAmount || 0).toLocaleString('es-CO')}</span>
                        </div>

                        {/* Pedidos Hijos (INT) */}
                        {order.internal_orders && order.internal_orders.length > 0 && order.internal_orders.map(intOrder => (
                            <div key={intOrder.id} className="flex justify-between items-center text-xs bg-amber-50 px-2 py-1 rounded border border-amber-100">
                                <div className="flex items-center gap-1">
                                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                                    <span className="text-amber-900 font-bold">#{intOrder.orderNumber}</span>
                                </div>
                                <span className="text-amber-900 font-bold">${Number(intOrder.totalAmount || 0).toLocaleString('es-CO')}</span>
                            </div>
                        ))}
                    </div>

                    {/* Resumen de Pago */}
                    <div className="flex items-end justify-between border-t border-dashed border-gray-200 pt-2">
                        <div className="flex flex-col gap-0.5">
                            {paidAmount > 0 && (
                                <div className="flex items-center gap-1">
                                    <span className="text-[10px] font-bold text-emerald-600 uppercase">Pagado</span>
                                    <span className="text-xs font-black text-emerald-700">${paidAmount.toLocaleString('es-CO')}</span>
                                </div>
                            )}
                            <div className="flex items-baseline gap-1.5">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">
                                    {hasBalance ? 'Cobrar' : 'Total'}
                                </span>
                                <span className={`text-lg font-black ${hasBalance ? 'text-red-600' : 'text-emerald-700'}`}>
                                    ${hasBalance ? totalDue.toLocaleString('es-CO') : totalAmount.toLocaleString('es-CO')}
                                </span>
                                {hasBalance && (
                                    <span className="bg-red-50 text-red-600 text-[9px] font-black px-1 rounded py-0.5 uppercase">
                                        Pendiente
                                    </span>
                                )}
                                {!hasBalance && paidAmount > 0 && (
                                    <span className="bg-emerald-50 text-emerald-600 text-[9px] font-black px-1 rounded py-0.5 uppercase">
                                        Pagado
                                    </span>
                                )}
                                {isCredit && (
                                    <span className="bg-blue-50 text-blue-600 text-[9px] font-black px-1 rounded py-0.5 uppercase">
                                        Crédito
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* WhatsApp Button Compacto */}
                        {order.customerPhone && !isDelivered && (
                            <a
                                href={`https://wa.me/57${order.customerPhone.replace(/\D/g, '')}`}
                                target="_blank"
                                rel="noreferrer"
                                className="p-2 rounded-full bg-green-50 text-green-600 hover:bg-green-100 transition-colors shadow-sm"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <MessageCircle className="w-5 h-5" />
                            </a>
                        )}
                    </div>
                </div>
            </div>

            {/* Footer: Acciones Compactas */}
            {!isDelivered && !isFailed && !isReturned && route.status === 'in_progress' ? (
                <div className="flex items-center justify-between gap-2 p-2 bg-gray-50 border-t border-gray-100">
                    {/* Drag Handle a la izquierda */}
                    {canDrag && (
                        <div
                            {...listeners}
                            className="p-1.5 text-gray-300 cursor-grab active:cursor-grabbing hover:text-gray-500 rounded hover:bg-gray-100 touch-none"
                        >
                            <GripVertical className="w-4 h-4" />
                        </div>
                    )}

                    <div className="flex items-center gap-2 ml-auto">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedOrderForFailed(order);
                            }}
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Reportar Fallo"
                        >
                            <AlertCircle className="w-4 h-4" />
                        </button>

                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedOrderDetails(order);
                            }}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Ver Detalles"
                        >
                            <Eye className="w-4 h-4" />
                        </button>

                        {/* Botón Entregar: Más compacto y elegante */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedOrderTimeline({ ...order, mode: 'delivery' });
                            }}
                            className="
                                flex items-center gap-1.5 
                                bg-emerald-600 text-white 
                                py-1.5 px-3 rounded-lg 
                                font-black text-xs uppercase tracking-wide 
                                shadow-sm hover:bg-emerald-700 active:scale-[0.98] transition-all ml-1
                            "
                        >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Entregar
                        </button>
                    </div>
                </div>
            ) : isReturned ? (
                <div className="p-2 bg-purple-600 flex justify-center items-center gap-1.5 text-white font-black text-xs uppercase tracking-wide">
                    <RotateCcw className="w-4 h-4" />
                    RETORNADO
                </div>
            ) : isFailed ? (
                <div className="p-2 bg-amber-50 border-t border-amber-200 flex justify-between items-center gap-2">
                    <div className="flex items-center gap-1.5 text-amber-700 font-black text-xs uppercase tracking-wide">
                        <AlertTriangle className="w-4 h-4" />
                        ENTREGA FALLIDA
                    </div>
                    {onValidateReturn && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onValidateReturn(order);
                            }}
                            className="flex items-center gap-1 bg-purple-600 text-white py-1.5 px-3 rounded-lg font-bold text-[10px] uppercase hover:bg-purple-700 transition-colors shadow-sm"
                        >
                            <RotateCcw className="w-3.5 h-3.5" />
                            Validar Retorno
                        </button>
                    )}
                </div>
            ) : isDelivered ? (
                <div className="p-2 bg-emerald-600 flex justify-center items-center gap-1.5 text-white font-black text-xs uppercase tracking-wide">
                    <CheckCircle className="w-4 h-4" />
                    ENTREGADO
                </div>
            ) : null}
        </div>
    );
};

export default SortableOrderCard;
